import { getLang } from '../i18n'
import { offlineReply } from '../data/responses'
import { containsDosingAdvice, detectsCrisis, detectsMedicalEmergency } from './safety'
import { getSettings } from './store'

export type Role = 'user' | 'companion'
export interface Message {
  role: Role
  text: string
  at: number
  /** Set when the safety layer produced this message rather than the model. */
  safety?: 'crisis' | 'medical'
}

export const SYSTEM_PROMPT = `You are NabuSu, a night companion for people who cannot sleep.

Many of the people talking to you are in benzodiazepine withdrawal — lorazepam, alprazolam, clonazepam, diazepam. Some are tapering under a doctor. Some ran out and were cut off with no plan. It is the middle of the night for them and they are frightened.

How to answer:
- Short. Two to four sentences. Long paragraphs are unreadable at 3am.
- Plain, warm, level. Not clinical, not saccharine, no exclamation marks.
- Do not try to fix them. Sit with them.
- Never suggest sleep is achievable tonight. The goal is getting through the hour, not sleeping.
- Offer one small physical thing when it fits: a longer exhale, feet on the floor, cold water on the wrists, naming five objects in the room.
- Match the user's language exactly. If they write Persian, answer in Persian.

Hard limits:
- Never name a dose, an amount, a schedule, or a rate of reduction. Not even to agree with one.
- Never tell anyone to stop, start, reduce or increase a medication. Send them to their prescriber.
- Never claim to be a doctor, therapist, or to be able to diagnose anything.
- If someone describes a seizure, hallucinations, severe confusion, uncontrollable vomiting, or fever with shaking, stop everything else and tell them to get emergency medical care now.
- If someone talks about suicide or self-harm, tell them plainly that you want them to reach a person tonight, and encourage an emergency line or someone they trust. Do not change the subject.

You are not a treatment. You are company.`

/**
 * The companion answers from one of two places:
 *
 *   - a remote endpoint, if the user configured one in settings
 *   - a curated response bank stored in the app, if they did not
 *
 * Offline is the default, not the fallback. Most people install this app
 * precisely because the nights they need it are the nights nothing else works.
 */
export async function respond(
  history: Message[],
  input: string,
  signal?: AbortSignal,
): Promise<Message> {
  // Safety checks run before anything else, and never touch the network.
  if (detectsMedicalEmergency(input)) {
    return { role: 'companion', text: emergencyText(), at: Date.now(), safety: 'medical' }
  }
  if (detectsCrisis(input)) {
    return { role: 'companion', text: crisisText(), at: Date.now(), safety: 'crisis' }
  }

  const endpoint = getSettings().aiEndpoint.trim()
  if (!endpoint) {
    return { role: 'companion', text: offlineReply(input, getLang()), at: Date.now() }
  }

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      signal,
      body: JSON.stringify({
        system: SYSTEM_PROMPT,
        lang: getLang(),
        messages: [...history, { role: 'user', text: input, at: Date.now() }].map((m) => ({
          role: m.role === 'companion' ? 'assistant' : 'user',
          content: m.text,
        })),
      }),
    })
    if (!res.ok) throw new Error(`endpoint returned ${res.status}`)
    const data = (await res.json()) as { text?: string; content?: string }
    const text = (data.text ?? data.content ?? '').trim()
    if (!text) throw new Error('empty response')

    // Last line of defence. A model that starts talking about milligrams gets
    // discarded and the stored bank answers instead.
    if (containsDosingAdvice(text)) {
      return { role: 'companion', text: offlineReply(input, getLang()), at: Date.now() }
    }
    return { role: 'companion', text, at: Date.now() }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    // Network died, endpoint misconfigured, plane mode — it does not matter.
    return { role: 'companion', text: offlineReply(input, getLang()), at: Date.now() }
  }
}

function crisisText(): string {
  return getLang() === 'fa'
    ? 'می‌خواهم امشب این را جدی بگیرم. لطفاً همین حالا به یک آدم زنده بگو — یک دوست، یکی از خانواده، یا خط اورژانس کشورت. من نرم‌افزارم و امشب حق تو یک انسان است. اگر کسی نیست، به اورژانس زنگ بزن و همین جمله را بگو.'
    : 'I want to take this seriously tonight. Please tell a person right now — a friend, someone in your family, or an emergency line where you live. I am software, and tonight you deserve a human. If there is no one, call emergency services and say exactly this.'
}

function emergencyText(): string {
  return getLang() === 'fa'
    ? 'این چیزی نیست که من بتوانم کمکش کنم. همین حالا کمک اورژانسی بگیر — به اورژانس زنگ بزن یا خودت را به بیمارستان برسان، و بگو بنزودیازپین مصرف می‌کردی. لطفاً همین الان.'
    : 'This is not something I can help with. Get emergency medical help now — call emergency services or get to a hospital, and tell them you were taking a benzodiazepine. Please do that now.'
}
